import React, { useMemo, useCallback } from 'react';

import chunk from 'lodash/chunk';

import {
  NoResults,
  List,
  Stack,
  makeStyles,
  Divider,
} from '@gogame/components';

import Favorites from './Favorites';
import FavoritesContext from './FavoritesContext';
import NavigationItem from './NavigationItem';

const useStyles = makeStyles(() => {
  return {
    root: {},
    favorites: {
      minWidth: '280px',
      overflow: 'auto',
      height: 'calc(100vh - 80px)',
    },
    noRecords: {
      width: '100%',
    },
    columns: {
      overflow: 'auto',
      height: 'calc(100vh - 80px)',
      width: '100%',
    },
    column: {
      width: ({ columnCount }) => (columnCount === 1 ? '296px' : '280px'),
      position: 'relative',
      overflow: 'auto',
    },
  };
});

const renderChildRoutes = (
  { link, externalLink, title, id, linkable, children },
  initialOpen,
  level,
  onClose
) => {
  return (
    <NavigationItem
      onClose={onClose}
      key={`${id}_${title}_${level}`}
      link={link}
      externalLink={externalLink}
      title={title}
      linkable={linkable}
      id={id}
      level={level}
      initialOpen={initialOpen}
      children={
        children && renderNavItems(children, initialOpen, level + 1, onClose)
      }
    />
  );
};

const renderNavItems = (pages, initialOpen, level = 0, onClose) => {
  return (
    <List
      disablePadding
      style={{
        paddingBottom: level === 0 ? 0 : '8px',
      }}
    >
      {pages.map((page) =>
        renderChildRoutes(page, initialOpen, level, onClose)
      )}
    </List>
  );
};

const flatNavigation = (navigation = []) => {
  return navigation.reduce((pages, itemWithChildren) => {
    const { children, ...item } = itemWithChildren;
    return [...pages, item, ...flatNavigation(children)];
  }, []);
};

function getVisibleItems(items, match) {
  return items.reduce((prev, item) => {
    const itemClone = { ...item };

    if (!match(item)) {
      return prev;
    }

    if (itemClone.children) {
      itemClone.children = getVisibleItems(itemClone.children, match);
    }

    return [...prev, itemClone];
  }, []);
}

const NavigationMenu = ({
  navigation,
  favoriteIds = [],
  onChangeFavorites,
  columnCount = 3,
  withFavorite,
  searchQuery = '',
  onClose,
}) => {
  const classes = useStyles({ columnCount });

  const flattenNavigation = useMemo(
    () => flatNavigation(navigation),
    [navigation]
  );
  const favoritePages = useMemo(() => {
    return favoriteIds
      .map((id) => {
        return flattenNavigation.find((page) => page.id === id);
      })
      .filter((item) => item);
  }, [favoriteIds, flattenNavigation]);

  const handleToggleFavorite = (id) => {
    return onChangeFavorites?.(
      favoriteIds.includes(id)
        ? favoriteIds.filter((favoriteId) => favoriteId !== id)
        : [...favoriteIds, id]
    );
  };

  const handleChangeFavorites = (newFavoritePages) => {
    onChangeFavorites?.(newFavoritePages.map(({ id }) => id));
  };

  const handleIsFavorite = (id) => favoriteIds.includes(id);

  const matchSearchQuery = useCallback(
    (searchKey) =>
      !searchQuery || searchKey.includes(searchQuery.toLowerCase()),
    [searchQuery]
  );

  const columns = useMemo(() => {
    return chunk(navigation, Math.ceil(navigation.length / columnCount))
      .map((col) =>
        getVisibleItems(col, ({ searchKey }) => matchSearchQuery(searchKey))
      )
      .filter((col) => col.length);
  }, [navigation, matchSearchQuery, columnCount]);
  return (
    <Stack direction={'row'}>
      {withFavorite ? (
        <Stack className={classes.favorites}>
          <Favorites
            pages={favoritePages}
            onChange={handleChangeFavorites}
            onClose={onClose}
          />
        </Stack>
      ) : null}

      <Stack direction={'row'} className={classes.columns}>
        {columns && columns.length ? (
          <FavoritesContext.Provider
            value={{
              withFavorite: withFavorite,
              toggle: handleToggleFavorite,
              isFavorite: handleIsFavorite,
            }}
          >
            {columns.map((items, i) => {
              const isFirstDividerVisible = withFavorite || !!i;
              return (
                <React.Fragment key={i}>
                  {isFirstDividerVisible && (
                    <Divider flexItem orientation={'vertical'} />
                  )}
                  <Stack className={classes.column}>
                    {renderNavItems(items, !!searchQuery, null, onClose)}
                  </Stack>
                </React.Fragment>
              );
            })}
          </FavoritesContext.Provider>
        ) : (
          <Stack
            align={'center'}
            justifyContent={'center'}
            className={classes.noRecords}
          >
            <NoResults inited={true} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default NavigationMenu;
